// ============================================================================
// JARVISTRAVEL - FLIGHTS API (model-ready seam)
// Live mode reads the trip's stored flights from the backend, which owns the
// provider calls (FAA ASWS airport status, BTS on-time history, AeroDataBox
// worldwide per-flight data) plus the refresh scheduler — see FLIGHTS-BACKEND.md
// (PR 2). Demo mode synthesizes a deterministic bundle from the client-side
// engine so the widget is fully usable without a backend. JT-FL-002.
// ============================================================================

import { api, isDemoSession } from './client';
import {
  demoTripFlights, demoFlightFromNumber, demoFlightFromRoute,
  extractFlightHints, parseFlightNumber, bestTime, type FlightHint,
} from '@/app/utils/flightEngine';
import { readStoredFlights, writeStoredFlights, upsertStoredFlight, patchStoredFlight, storedBundle } from './flightsStore';
import { isBackendTripID } from '@/app/utils/tripIds';
import type { TripFlight, TripFlights, FlightMovement } from '@/app/types';

/** One service a flight number could mean around the trip (JAR-2321). Core
 *  returns up to three when a number is ambiguous; `date` is the service's real
 *  DEPARTURE date, and the movements reuse the `FlightMovement` shape so the
 *  chooser renders with the same helpers as a leg. */
export interface FlightCandidate {
  date: string;
  departure: FlightMovement;
  arrival: FlightMovement;
}

/** The result of adding a flight by number. Either core resolved it to one
 *  flight, or the number runs more than once around the trip and core is asking
 *  which. Supplying a date always resolves (core never asks when given one), so
 *  the re-pick below can only come back `added`. */
export type AddFlightOutcome =
  | { kind: 'added'; flight: TripFlight }
  | { kind: 'ambiguous'; candidates: FlightCandidate[] };

/** The 200 body core sends when a number is ambiguous (JAR-2319, 15:22Z). A
 *  resolved add is a bare TripFlight with no `candidates` key, so the presence
 *  of `candidates` is the discriminator. */
interface AmbiguousAddBody {
  code: string;
  error?: string;
  candidates: FlightCandidate[];
}

/** The minimum a caller passes so demo can synthesize and live can address the
 *  trip. Live only needs `id`; demo uses `destination` + `dates`. */
export interface FlightsLookup {
  id: string;
  destination: string;
  dates: { checkin: string; checkout: string };
  /** When the trip's plan stops taking changes. Past it, useFlights refuses
   *  every change and pull itself, as core would (JAR-2168). */
  planningClosesAt?: string;
}

/** Minimal shape guard on a live response — a malformed payload should surface
 *  as an error, not crash the widget on `flights.map`. */
function isValidTripFlights(v: unknown): v is TripFlights {
  if (!v || typeof v !== 'object') return false;
  const t = v as Record<string, unknown>;
  if (typeof t.tripId !== 'string' || typeof t.updatedAt !== 'string' || !Array.isArray(t.flights)) return false;
  // Nested critical paths: every flight needs a legs array, every leg needs
  // movement objects with a scheduled time — the fields components index into.
  return (t.flights as unknown[]).every((f) => {
    if (!f || typeof f !== 'object' || !Array.isArray((f as { legs?: unknown }).legs)) return false;
    return ((f as { legs: unknown[] }).legs).every((l) => {
      const leg = l as { departure?: { scheduled?: unknown }; arrival?: { scheduled?: unknown }; flightNumber?: unknown };
      return typeof leg?.flightNumber === 'string'
        && typeof leg?.departure?.scheduled === 'string'
        && typeof leg?.arrival?.scheduled === 'string';
    });
  });
}

/** Demo bundle: the user's own flights (intake/manual) when stored, else the
 *  synthesized round trip. */
function demoBundle(trip: FlightsLookup): TripFlights {
  const stored = readStoredFlights(trip.id);
  if (stored) return storedBundle(trip.id, stored);
  return demoTripFlights(trip.id, trip.destination, trip.dates.checkin, trip.dates.checkout);
}

/** Current stored flights for a trip. Live, a trip only this browser holds has
 *  none: its id isn't core's, and core refuses it with a 400, so nothing is
 *  asked (JAR-2985). */
export async function getTripFlights(trip: FlightsLookup): Promise<TripFlights | null> {
  if (isDemoSession()) return demoBundle(trip);
  if (!isBackendTripID(trip.id)) return null;
  const result = await api.get<TripFlights>(`/trips/${encodeURIComponent(trip.id)}/flights`);
  if (result.error) throw new Error(result.error.message || 'Failed to load flights');
  if (!isValidTripFlights(result.data)) throw new Error('Malformed flights response from server');
  return result.data;
}

/** Force a fresh provider pull (card open / widget tap when a pull is due).
 *  None for a trip only this browser holds, as above. */
export async function refreshTripFlights(trip: FlightsLookup): Promise<TripFlights | null> {
  if (isDemoSession()) return demoBundle(trip);
  if (!isBackendTripID(trip.id)) return null;
  const result = await api.post<TripFlights>(`/trips/${encodeURIComponent(trip.id)}/flights/refresh`);
  if (result.error) throw new Error(result.error.message || 'Couldn’t refresh flights');
  if (!isValidTripFlights(result.data)) throw new Error('Malformed flights response from server');
  return result.data;
}

/** Manual add: the user types a flight number; the service resolves it against
 *  registered flights and returns a `matched` flight awaiting confirmation. */
export async function addFlightByNumber(trip: FlightsLookup, input: string, date?: string): Promise<AddFlightOutcome> {
  const parsed = parseFlightNumber(input);
  if (!parsed) throw new Error('That doesn’t look like a flight number (e.g. AZ 610).');
  if (isDemoSession()) {
    // The date control is gone (JAR-2321), so the person types only a number.
    // Demo has no ambiguity to resolve, so it just synthesizes the one flight;
    // without a date it uses the trip's first day, as the old default did.
    const flight = demoFlightFromNumber(trip.id, trip.destination, parsed.normalized, date || trip.dates.checkin);
    if (!flight) throw new Error('That doesn’t look like a flight number (e.g. AZ 610).');
    // Persist only when the trip already has user flights — a synthesized demo
    // bundle stays ephemeral (the store must not shadow it with one flight).
    if (readStoredFlights(trip.id)) upsertStoredFlight(trip.id, flight);
    return { kind: 'added', flight };
  }
  // `date` is omitted on the first add (core works the day out from the trip) and
  // sent only when re-picking a chosen candidate — where it means the DEPARTURE
  // date, and core then resolves that service rather than asking again (JAR-2319).
  const result = await api.post<TripFlight | AmbiguousAddBody>(
    `/trips/${encodeURIComponent(trip.id)}/flights`,
    { flightNumber: parsed.normalized, ...(date ? { date } : {}) },
  );
  if (result.error) {
    // A number that resolves to nothing is a 404 the person can act on; keep the
    // other errors generic.
    throw new Error(
      result.error.detailCode === 'FLIGHT_NOT_FOUND'
        ? 'We couldn’t find that flight number. Check it and try again.'
        : result.error.message || 'Couldn’t add that flight',
    );
  }
  if (!result.data) throw new Error('Empty response from server');
  const data = result.data;
  // Ambiguous: a 200 carrying `candidates` rather than a bare TripFlight. Branch
  // on the field's presence, since a resolved flight never has it.
  if ('candidates' in data && Array.isArray(data.candidates)) {
    return { kind: 'ambiguous', candidates: data.candidates };
  }
  return { kind: 'added', flight: data as TripFlight };
}

/** The flight's own service as a single candidate — the demo has no provider to
 *  find nearby days, so "Different day?" offers only the service already shown. */
function currentAsCandidate(flight: TripFlight): FlightCandidate {
  return {
    date: bestTime(flight.legs[0].departure).slice(0, 10),
    departure: flight.legs[0].departure,
    arrival: flight.legs[flight.legs.length - 1].arrival,
  };
}

/** The number's services near a flight, for the "Different day?" switch
 *  (JAR-2321). `candidates` is always present (core never nulls it, even for a
 *  number that flies only that one day), so no nil guard is needed. */
export async function fetchOtherDays(trip: FlightsLookup, flightId: string): Promise<FlightCandidate[]> {
  if (isDemoSession()) {
    // No provider in demo, so the only service on offer is the one already shown.
    const flight = demoBundle(trip).flights.find((f) => f.id === flightId);
    return flight && flight.legs.length > 0 ? [currentAsCandidate(flight)] : [];
  }
  const result = await api.get<{ candidates: FlightCandidate[] }>(
    `/trips/${encodeURIComponent(trip.id)}/flights/${encodeURIComponent(flightId)}/other-days`,
  );
  if (result.error) {
    // A browse action, so keep the raw provider string out of the toast.
    throw new Error(
      result.error.detailCode === 'FLIGHT_NOT_FOUND'
        ? 'That flight isn’t on this trip anymore.'
        : 'Couldn’t load other days for that flight.',
    );
  }
  return result.data?.candidates ?? [];
}

/** Switch a flight to a nearby service IN PLACE (JAR-2321): same id, new service
 *  date and legs, confirm state and kind preserved, marked settled so it survives
 *  the next refresh. A dedicated route, NOT the add — the add's id is date-derived,
 *  so re-adding would leave a second row (measured on core). */
export async function switchFlightDay(trip: FlightsLookup, flightId: string, date: string): Promise<TripFlight> {
  if (isDemoSession()) {
    // The only demo candidate is the current service, so this is a no-op; return
    // the flight as it stands rather than synthesizing a different one.
    const flight = demoBundle(trip).flights.find((f) => f.id === flightId);
    if (!flight) throw new Error('That flight isn’t on this trip anymore.');
    return flight;
  }
  const result = await api.post<TripFlight>(
    `/trips/${encodeURIComponent(trip.id)}/flights/${encodeURIComponent(flightId)}/day`,
    { date },
  );
  if (result.error) {
    throw new Error(
      result.error.detailCode === 'FLIGHT_NOT_FOUND'
        ? 'That flight isn’t on this trip anymore.'
        : result.error.detailCode === 'FLIGHT_DAY_NOT_FOUND'
          ? 'That flight number doesn’t fly on that day.'
          : 'Couldn’t switch to that day.',
    );
  }
  if (!result.data) throw new Error('Empty response from server');
  return result.data;
}

/** Lock in a matched flight — from here the service keeps it fresh. */
export async function confirmFlight(trip: FlightsLookup, flightId: string): Promise<void> {
  if (isDemoSession()) {
    patchStoredFlight(trip.id, flightId, { confirmState: 'confirmed' }); // no-op without a store
    return;
  }
  const result = await api.post(`/trips/${encodeURIComponent(trip.id)}/flights/${encodeURIComponent(flightId)}/confirm`);
  if (result.error) throw new Error(result.error.message || 'Couldn’t confirm that flight');
}

/** Take a flight off the trip. Core works the days' jet lag out from the
 *  stored flights, so it stops counting this one. */
export async function removeFlight(trip: FlightsLookup, flightId: string): Promise<void> {
  if (isDemoSession()) {
    // The flights the demo shows, stored or synthesized, less this one. An empty
    // list is stored too, so removing the last doesn't bring the synthesized back.
    writeStoredFlights(trip.id, demoBundle(trip).flights.filter((f) => f.id !== flightId));
    return;
  }
  const result = await api.del(`/trips/${encodeURIComponent(trip.id)}/flights/${encodeURIComponent(flightId)}`);
  if (result.error) throw new Error(result.error.message || 'Couldn’t remove that flight');
}

/** Resolve one intake hint (flight number or route/time phrase) against
 *  registered flights. Null = no candidate at all. */
export async function matchFlight(trip: FlightsLookup, hint: FlightHint): Promise<TripFlight | null> {
  if (isDemoSession()) {
    if (hint.kind === 'number') {
      return demoFlightFromNumber(trip.id, trip.destination, hint.flightNumber, trip.dates.checkin);
    }
    // Date heuristic for route hints: a chain ENDING at the destination is the
    // inbound (check-in day); one STARTING there is the return (check-out day).
    // The live service resolves real operating dates (FLIGHTS-BACKEND.md).
    const destCity = trip.destination.split(',')[0].trim().toLowerCase();
    const first = hint.cities[0]?.toLowerCase() ?? '';
    const leavesDestination = first === destCity || first.startsWith(`${destCity} `);
    const date = leavesDestination ? trip.dates.checkout : trip.dates.checkin;
    return demoFlightFromRoute(trip.id, hint.cities, date, hint.arrivalTime);
  }
  const result = await api.post<TripFlight | null>(`/trips/${encodeURIComponent(trip.id)}/flights/match`, hint);
  if (result.error) throw new Error(result.error.message || 'Couldn’t match that flight');
  return result.data ?? null;
}

/** Planner intake: extract flight hints from what the user typed, match each,
 *  and attach the results to the trip (`matched` populate the widget;
 *  `unverified` carry the confirm icon). Returns the attached flights. */
export async function intakeFlights(trip: FlightsLookup, text: string): Promise<TripFlight[]> {
  const hints = extractFlightHints(text);
  if (hints.length === 0) return [];
  if (isDemoSession()) {
    const flights: TripFlight[] = [];
    for (const hint of hints) {
      const flight = await matchFlight(trip, hint);
      if (flight) flights.push(flight);
    }
    // Upsert — a planner re-run must not wipe manually-added flights (same
    // semantics as addFlightByNumber).
    for (const flight of flights) upsertStoredFlight(trip.id, flight);
    return flights;
  }
  const result = await api.post<TripFlight[]>(`/trips/${encodeURIComponent(trip.id)}/flights/intake`, { text });
  if (result.error) throw new Error(result.error.message || 'Couldn’t process flight details');
  return result.data ?? [];
}
